/**
 * Selection state for the funding-source type picker.
 *
 * Starts from the host's saved selection merged with every type the client already has a
 * self-funding source for; once the user toggles a card, their explicit choices win. Feature flags
 * decide which cards exist. Nothing here writes funding sources: the host decides what to do with
 * the selection when the user continues.
 */
import { useCallback, useMemo, useState } from 'react';

import { useFeatureFlags } from '@client/contexts/FeatureFlagsContext';

import {
  ASSET_TYPES,
  ASSET_TYPE_TO_ACCOUNT_TYPE,
  filterForRetirementIncomeExpanded,
  type AssetTypeId,
} from '../config/funding-source-types';

export function useFundingSourceSelection(
  existingSelfFundingSources: Record<string, SelfFundingSource> | undefined,
  savedSelection: readonly AssetTypeId[] | null,
) {
  const { flags } = useFeatureFlags();
  const [localOverrides, setLocalOverrides] = useState<Set<AssetTypeId> | null>(null);

  const typesToShow = useMemo(() => {
    const base = flags.existingLtcPolicyTypeOnCard
      ? ASSET_TYPES
      : ASSET_TYPES.filter(a => a.id !== 'existing-ltc-policy');
    return filterForRetirementIncomeExpanded(base, flags.retirementIncomeExpanded);
  }, [flags.existingLtcPolicyTypeOnCard, flags.retirementIncomeExpanded]);

  // Types that already have a funding source (added from the funding table or elsewhere)
  const typesWithFundingSource = useMemo(() => {
    const accountTypes = new Set(
      Object.values(existingSelfFundingSources ?? {})
        .map(s => s.accountType)
        .filter(Boolean) as string[],
    );
    const result = new Set<AssetTypeId>();
    for (const [typeId, accountType] of Object.entries(ASSET_TYPE_TO_ACCOUNT_TYPE)) {
      if (accountTypes.has(accountType)) {
        result.add(typeId as AssetTypeId);
      }
    }
    return result;
  }, [existingSelfFundingSources]);

  const savedSet = useMemo(() => new Set<AssetTypeId>(savedSelection ?? []), [savedSelection]);

  const selected = useMemo(() => {
    if (localOverrides) return localOverrides;
    const merged = new Set(savedSet);
    typesWithFundingSource.forEach(id => merged.add(id));
    return merged;
  }, [localOverrides, savedSet, typesWithFundingSource]);

  const toggle = useCallback(
    (id: AssetTypeId) => {
      const next = new Set(localOverrides ?? new Set([...savedSet, ...typesWithFundingSource]));
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      setLocalOverrides(next);
    },
    [localOverrides, savedSet, typesWithFundingSource],
  );

  return { typesToShow, selected, toggle, hasSelection: selected.size > 0 };
}
